import googleAdsLogo from '/google-ads.svg';
import metaLogo from '/meta.png';
import { useAtomValue } from 'jotai';
import { RefreshCw } from 'lucide-react';

import { FilterDropdown, type FilterDropdownState, type FilterSection } from '@/components/glitch-ui/filter-dropdown';
import { Spinner } from '@/components/glitch-ui/spinner';
import { Button } from '@/components/ui/button';
import { HIDDEN_TYPES } from '@/hooks/useCampaignSidebarFilters';
import { toast } from '@/hooks/useToast';
import { campaignCheckMessage, type SidebarFilterChoices } from '@/lib/google-ads-campaign-check';
import { useCheckGoogleAdsForCampaignsMutation } from '@/lib/query-options';
import { googleAdsAccountAtom, organizationDataAtom } from '@/store';

/**
 * The campaigns sidebar's filter sections, shared by every page that shows the sidebar.
 *
 * ``showPlatforms`` adds the Platform section (Google and Meta); the campaigns pages pass it.
 */
export function buildSidebarFilterSections(showPlatforms: boolean): FilterSection[] {
  return [
    ...(showPlatforms
      ? [
          {
            id: 'platforms',
            title: 'Platform',
            options: [
              { value: 'google', label: 'Google', icon: <img src={googleAdsLogo} alt="" className="h-4 w-4" /> },
              { value: 'meta', label: 'Meta', icon: <img src={metaLogo} alt="" className="w-5 h-auto" /> },
            ],
          },
        ]
      : []),
    {
      id: 'statuses',
      title: 'Status',
      options: [
        { value: 'live', label: 'Live' },
        { value: 'draft', label: 'Draft' },
        { value: 'paused', label: 'Paused' },
        { value: 'ended', label: 'Ended' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      id: 'types',
      title: 'Type',
      options: [
        { value: 'brand', label: 'Brand' },
        { value: 'prospecting', label: 'Prospecting' },
        { value: 'competitor', label: 'Competitor' },
        { value: 'hipo', label: 'High Performance' },
        { value: 'icp', label: 'ICP' },
        { value: 'location', label: 'Location' },
        { value: 'external', label: 'External' },
        { value: 'retargeting', label: 'Retargeting' },
      ],
    },
  ];
}

interface CampaignSidebarToolbarProps {
  filterTypes: Set<string>;
  filterStatuses: Set<string>;
  filterPlatforms: Set<string>;
  /** Show the Platform section. Without it, the saved platforms are kept rather than cleared. */
  showPlatforms: boolean;
  onFiltersChange: (types: Set<string>, statuses: Set<string>, platforms: Set<string>) => void;
}

const CHECK_GOOGLE_ADS_LABEL = 'Check Google Ads for campaigns';

/**
 * Check the selected Google Ads account for campaigns to list, when a person asks (#6612).
 *
 * Selecting an account no longer checks Google Ads, so this is how a person brings campaigns created there into the
 * list. It lists them as External and never imports. Shown once an organization and a Google Ads account are selected.
 */
function CheckGoogleAdsCampaignsButton({ filters }: { filters: SidebarFilterChoices }) {
  const organization = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const check = useCheckGoogleAdsForCampaignsMutation();

  if (!organization || !googleAdsAccount) return null;

  const runCheck = () =>
    check.mutate(organization.id, {
      onSuccess: (result) => toast({ description: campaignCheckMessage(result, filters) }),
      onError: (error) =>
        toast({ title: "Couldn't check Google Ads", description: error.message, variant: 'destructive' }),
    });

  return (
    <Button
      variant="outline"
      className="h-9 w-9 p-0 text-gray-900"
      onClick={runCheck}
      disabled={check.isPending}
      aria-label={CHECK_GOOGLE_ADS_LABEL}
      title={CHECK_GOOGLE_ADS_LABEL}
    >
      {check.isPending ? <Spinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
    </Button>
  );
}

/** The campaigns sidebar toolbar: the Google Ads campaign check and the campaign filter. */
export function CampaignSidebarToolbar({
  filterTypes,
  filterStatuses,
  filterPlatforms,
  showPlatforms,
  onFiltersChange,
}: CampaignSidebarToolbarProps) {
  return (
    <div className="flex items-center gap-1">
      <CheckGoogleAdsCampaignsButton
        filters={{ types: filterTypes, statuses: filterStatuses, platforms: filterPlatforms, showPlatforms }}
      />
      <FilterDropdown
        sections={buildSidebarFilterSections(showPlatforms)}
        selectedValues={{
          statuses: [...filterStatuses],
          types: [...filterTypes].filter((t) => !HIDDEN_TYPES.includes(t)),
          platforms: [...filterPlatforms],
        }}
        hiddenValues={{ types: HIDDEN_TYPES }}
        showDateRange={false}
        popoverWidth="w-[220px]"
        onApply={(filters: FilterDropdownState) => {
          onFiltersChange(
            new Set(filters.selections.types || []),
            new Set(filters.selections.statuses || []),
            showPlatforms ? new Set(filters.selections.platforms || []) : filterPlatforms,
          );
        }}
      />
    </div>
  );
}
